import React, { useEffect, useMemo, useRef, useState } from "react";
import "./style.css";

/**
 * @param {{
 * 	invoiceNumber: string,
 * 	date: Date | undefined,
 * 	terms: string,
 * 	onInvoiceNumberChange: (value: string) => void,
 * 	onDateChange: (date: Date | undefined) => void,
 * 	onTermsChange: (value: string) => void
 * }} props
 */
export default function InvoiceDetails({
	invoiceNumber,
	date,
	terms,
	onInvoiceNumberChange,
	onDateChange,
	onTermsChange
}) {
	const termsOptions = useMemo(
		() => [
			{ value: "due-on-receipt", label: "Due on Receipt" },
			{ value: "net-0", label: "Upon product delivery" },
			{ value: "net-7", label: "Net 7 Days" },
			{ value: "net-14", label: "Net 14 Days" },
			{ value: "net-30", label: "Net 30 Days" },
			{ value: "net-60", label: "Net 60 Days" },
			{ value: "net-90", label: "Net 90 Days" },
			{ value: "custom", label: "Custom" }
		],
		[]
	);

	const [open, setOpen] = useState(false);
	const popRef = useRef(null);

	const dateValue = date instanceof Date && !Number.isNaN(date.getTime()) ? date : undefined;

	const formattedDate = dateValue
		? new Intl.DateTimeFormat(undefined, { dateStyle: "medium" }).format(dateValue)
		: "";

	const inputDateValue = dateValue ? toInputDate(dateValue) : "";

	useEffect(() => {
		const onDocMouseDown = (e) => {
			if (!open) return;
			if (!popRef.current) return;
			if (popRef.current.contains(e.target)) return;
			setOpen(false);
		};

		document.addEventListener("mousedown", onDocMouseDown);
		return () => document.removeEventListener("mousedown", onDocMouseDown);
	}, [open]);

	const handleDateInputChange = (e) => {
		const value = e.target.value; // yyyy-mm-dd
		if (!value) {
			onDateChange(undefined);
			return;
		}
		const next = new Date(`${value}T00:00:00`);
		if (Number.isNaN(next.getTime())) {
			onDateChange(undefined);
			return;
		}
		onDateChange(next);
	};

	const clearDate = () => {
		onDateChange(undefined);
		setOpen(false);
	};

	return (
		<div className="invoice-section">
			<h3 className="invoice-section-title">Invoice Details</h3>

			<div className="invoice-details-grid">
				<div className="field">
					<label className="label" htmlFor="invoice-number">
						Invoice Number
					</label>
					<input
						id="invoice-number"
						className="invoice-input"
						type="text"
						placeholder="INV-001"
						value={invoiceNumber || ""}
						onChange={(e) => onInvoiceNumberChange(e.target.value)}
					/>
				</div>

				<div className="field">
					<label className="label">Invoice Date</label>

					<div className="date-popover" ref={popRef}>
						<button
							type="button"
							className={`invoice-input date-trigger ${!dateValue ? "is-muted" : ""}`}
							onClick={() => setOpen((v) => !v)}
							aria-haspopup="dialog"
							aria-expanded={open}
						>
							<span className="cal-icon" aria-hidden="true">
								{/* simple calendar icon (inline SVG) */}
								<svg viewBox="0 0 24 24" width="16" height="16">
									<path d="M7 2a1 1 0 0 1 1 1v1h8V3a1 1 0 1 1 2 0v1h1a3 3 0 0 1 3 3v13a3 3 0 0 1-3 3H5a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3h1V3a1 1 0 0 1 1-1Zm12 8H5v10a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V10ZM5 8h14V7a1 1 0 0 0-1-1h-1v1a1 1 0 1 1-2 0V6H8v1a1 1 0 0 1-2 0V6H5a1 1 0 0 0-1 1v1Z" />
								</svg>
							</span>

							{dateValue ? <span>{formattedDate}</span> : <span>Pick a date</span>}
						</button>

						{open ? (
							<div className="popover" role="dialog" aria-label="Choose invoice date">
								<div className="popover-row">
									<label className="sr-only" htmlFor="invoice-date-input">
										Invoice Date
									</label>
									<input
										id="invoice-date-input"
										className="invoice-input"
										type="date"
										value={inputDateValue}
										onChange={handleDateInputChange}
									/>
								</div>

								<div className="popover-actions">
									<button type="button" className="btn btn-ghost" onClick={clearDate}>
										Clear
									</button>
									<button type="button" className="btn" onClick={() => setOpen(false)}>
										Done
									</button>
								</div>
							</div>
						) : null}
					</div>
				</div>

				<div className="field">
					<label className="label" htmlFor="payment-terms">
						Payment Terms
					</label>

					<select
						id="payment-terms"
						className="invoice-input select"
						value={terms || ""}
						onChange={(e) => onTermsChange(e.target.value)}
					>
						<option value="" disabled>
							Select terms
						</option>
						{termsOptions.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</div>
			</div>
		</div>
	);
}

/** @param {Date} d */
function toInputDate(d) {
	const yyyy = String(d.getFullYear());
	const mm = String(d.getMonth() + 1).padStart(2, "0");
	const dd = String(d.getDate()).padStart(2, "0");
	return `${yyyy}-${mm}-${dd}`;
}
